import {
  formatArticleDate,
  getCategoryLabel,
  getSourceLabel,
  NEWS_CATEGORY_OPTIONS,
  NEWS_SOURCE_OPTIONS,
  NEWS_SOURCES,
  truncateExcerpt,
} from './newsConfig';

export default function NewsArticleTable({
  articles,
  loading,
  sourceFilter,
  categoryFilter,
  onSourceFilterChange,
  onCategoryFilterChange,
  onEdit,
  onDelete,
}) {
  const filteredArticles = articles.filter((article) => {
    const articleSource =
      article.source === NEWS_SOURCES.NATIONAL ? NEWS_SOURCES.NATIONAL : NEWS_SOURCES.LOCAL;
    if (sourceFilter !== 'all' && articleSource !== sourceFilter) return false;
    if (categoryFilter !== 'all' && article.category !== categoryFilter) return false;
    return true;
  });

  return (
    <div className="bg-white shadow-sm border border-stone-200 rounded-2xl p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-cpre-new-yew">Manage Articles</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {filteredArticles.length} article{filteredArticles.length === 1 ? '' : 's'} shown
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={sourceFilter}
            onChange={(e) => onSourceFilterChange(e.target.value)}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
          >
            <option value="all">All types</option>
            {NEWS_SOURCE_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => onCategoryFilterChange(e.target.value)}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
          >
            <option value="all">All categories</option>
            {NEWS_CATEGORY_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-muted-foreground">Loading articles...</div>
      ) : filteredArticles.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          No articles match the current filters.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-stone-200">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-stone-100">
              {filteredArticles.map((article) => {
                const articleSource =
                  article.source === NEWS_SOURCES.NATIONAL
                    ? NEWS_SOURCES.NATIONAL
                    : NEWS_SOURCES.LOCAL;

                return (
                  <tr key={article.id} className="hover:bg-stone-50 align-top">
                    <td className="px-4 py-4 max-w-md">
                      <div className="text-sm font-medium text-cpre-new-yew">{article.title}</div>
                      <div className="text-sm text-muted-foreground mt-1 break-words">
                        {truncateExcerpt(article.excerpt)}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                          articleSource === NEWS_SOURCES.NATIONAL
                            ? 'bg-cpre-hill/15 text-cpre-hill'
                            : 'bg-cpre-harvest-gold/20 text-cpre-new-yew'
                        }`}
                      >
                        {getSourceLabel(articleSource)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full bg-stone-100 text-stone-700">
                        {getCategoryLabel(article.category)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {article.published ? (
                        <span className="text-green-700 font-medium">Published</span>
                      ) : (
                        <span className="text-stone-600 font-medium">Draft</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-stone-600 whitespace-nowrap">
                      {formatArticleDate(article.created_at)}
                    </td>
                    <td className="px-4 py-4 text-right text-sm font-medium whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => onEdit(article)}
                        className="text-cpre-hill hover:text-cpre-new-yew mr-4"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(article.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
